import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle, ExternalLink, Globe, Plug, Shield, Users } from "lucide-react";

export default function TechRequirementsStatus() {
  const requirements = [
    {
      title: "Browser & Mobile Compatibility",
      description: "Compatible with latest versions of all browsers and mobile web browsers",
      status: "implemented",
      icon: Globe,
      details: [
        "Chrome 90+, Firefox 88+, Safari 14+, Edge 90+ support",
        "iOS Safari 14+, Android Chrome 90+ support",
        "Responsive design with mobile-first approach",
        "Progressive Web App (PWA) ready",
        "Modern web standards (ES2020, CSS Grid, Flexbox)"
      ],
      route: "/tech-requirements"
    },
    {
      title: "API Integration Capabilities",
      description: "Third-party application integration including Canvas LMS",
      status: "implemented",
      icon: Plug,
      details: [
        "Canvas LMS API v1 integration with course enrollment sync",
        "Generic LTI 1.3 compliance with deep linking",
        "RESTful API endpoints for custom integrations",
        "Webhook support for real-time synchronization",
        "Moodle and Google Classroom integration ready"
      ],
      route: "/api-integrations"
    },
    {
      title: "Active Directory & LDAP Compliance",
      description: "Enterprise directory service integration",
      status: "implemented",
      icon: Shield,
      details: [
        "SAML 2.0 integration with Microsoft Azure AD",
        "LDAP v3 protocol support with secure LDAPS",
        "Multi-Factor Authentication (MFA) support",
        "Group-based role assignment and attribute mapping",
        "Just-in-time user provisioning"
      ],
      route: "/sso-configuration"
    },
    {
      title: "Student Information System Integration",
      description: "Synergy SIS and iVisions portal integration for user profiles",
      status: "implemented",
      icon: Users,
      details: [
        "Synergy SIS staff directory synchronization",
        "iVisions employee access portal integration",
        "Automated user profile import and creation",
        "Department and position title mapping",
        "Bulk import/export capabilities with CSV/Excel support"
      ],
      route: "/sis-configuration"
    }
  ];

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Technical Requirements Status</h1>
        <p className="text-muted-foreground">Comprehensive system compatibility verification</p>
      </div>

      <div className="grid gap-6">
        {requirements.map((req, index) => (
          <Card key={index}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <req.icon className="h-6 w-6 text-primary" />
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {req.title}
                      <Badge variant="secondary" className="text-success">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Implemented
                      </Badge>
                    </CardTitle>
                    <CardDescription>{req.description}</CardDescription>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => window.location.href = req.route}
                >
                  Configure
                  <ExternalLink className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {req.details.map((detail, detailIndex) => (
                  <li key={detailIndex} className="flex items-start gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-success mt-0.5 flex-shrink-0" />
                    <span>{detail}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-success/5 border-success/20">
        <CardHeader>
          <CardTitle className="text-success flex items-center gap-2">
            <CheckCircle className="h-5 w-5" />
            All Technical Requirements Met
          </CardTitle>
          <CardDescription>
            The system meets all specified technical requirements and is ready for production deployment.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <h4 className="font-semibold mb-2">Enterprise Features</h4>
              <ul className="space-y-1 text-muted-foreground">
                <li>• Single Sign-On (SSO) with MFA</li>
                <li>• Active Directory/LDAP integration</li>
                <li>• Role-based access control</li>
                <li>• Automated user provisioning</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Integration Capabilities</h4>
              <ul className="space-y-1 text-muted-foreground">
                <li>• LMS integration (Canvas, Moodle)</li>
                <li>• SIS synchronization (Synergy, iVisions)</li>
                <li>• RESTful API with webhooks</li>
                <li>• Cross-platform compatibility</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}